/**
 * CTADrawer — Universal right-side slide-out drawer for POC form CTAs
 * Obsidian Pulse design system — CHI Alliance
 *
 * Variants:
 *  "speaker"         → Apply to Speak
 *  "join-community"  → Join the Community
 *  "general-inquiry" → General Inquiry / Contact Us
 */

import { useEffect, useRef, useState } from "react";
import { X, CheckCircle, Mic, Users, MessageSquare } from "lucide-react";
import { chiNotify } from "@/components/ChiNotification";
import CtaIcon from "@/components/CtaIcon";

export type CTADrawerVariant = "speaker" | "join-community" | "general-inquiry";

interface CTADrawerProps {
  open: boolean;
  onClose: () => void;
  variant: CTADrawerVariant;
}

const VARIANTS: Record<
  CTADrawerVariant,
  {
    icon: React.ReactNode;
    label: string;
    title: string;
    subtitle: string;
    accentColor: string;
    gradientFrom: string;
    gradientTo: string;
    submitLabel: string;
  }
> = {
  speaker: {
    icon: <Mic size={18} />,
    label: "Speaker Application",
    title: "Apply to Speak",
    subtitle:
      "Share your expertise with a community of 500+ HR leaders, innovators, and business professionals. Tell us about yourself and your topic.",
    accentColor: "#E91E8C",
    gradientFrom: "#E91E8C",
    gradientTo: "#F5A623",
    submitLabel: "Submit Application",
  },
  "join-community": {
    icon: <Users size={18} />,
    label: "Community Membership",
    title: "Join the CHI Alliance",
    subtitle:
      "Become part of a trusted community where professionals connect, learn, collaborate, and grow together.",
    accentColor: "#00D4FF",
    gradientFrom: "#00D4FF",
    gradientTo: "#E91E8C",
    submitLabel: "Request Membership",
  },
  "general-inquiry": {
    icon: <MessageSquare size={18} />,
    label: "General Inquiry",
    title: "Get in Touch",
    subtitle:
      "Have a question or want to learn more about The CHI Alliance? We'd love to hear from you.",
    accentColor: "#F5A623",
    gradientFrom: "#F5A623",
    gradientTo: "#E91E8C",
    submitLabel: "Send Message",
  },
};

function FormInput({
  label,
  type = "text",
  placeholder,
  required,
  accentColor,
}: {
  label: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  accentColor: string;
}) {
  return (
    <div>
      <label
        style={{
          display: "block",
          color: "#94A3B8",
          fontSize: "0.78rem",
          fontFamily: "'Space Grotesk', sans-serif",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginBottom: "0.4rem",
        }}
      >
        {label} {required && <span aria-hidden="true" style={{ color: accentColor }}>*</span>}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        required={required}
        aria-required={required || undefined}
        style={{
          width: "100%",
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "0.6rem",
          padding: "0.7rem 0.9rem",
          color: "white",
          fontFamily: "'Inter', sans-serif",
          fontSize: "0.9rem",
          outline: "none",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          boxSizing: "border-box",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = accentColor + "80";
          e.currentTarget.style.boxShadow = `0 0 0 3px ${accentColor}18`;
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
          e.currentTarget.style.boxShadow = "none";
        }}
      />
    </div>
  );
}

function FormTextarea({
  label,
  placeholder,
  rows = 4,
  required,
  accentColor,
}: {
  label: string;
  placeholder?: string;
  rows?: number;
  required?: boolean;
  accentColor: string;
}) {
  return (
    <div>
      <label
        style={{
          display: "block",
          color: "#94A3B8",
          fontSize: "0.78rem",
          fontFamily: "'Space Grotesk', sans-serif",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginBottom: "0.4rem",
        }}
      >
        {label} {required && <span aria-hidden="true" style={{ color: accentColor }}>*</span>}
      </label>
      <textarea
        rows={rows}
        placeholder={placeholder}
        required={required}
        aria-required={required || undefined}
        style={{
          width: "100%",
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "0.6rem",
          padding: "0.7rem 0.9rem",
          color: "white",
          fontFamily: "'Inter', sans-serif",
          fontSize: "0.9rem",
          outline: "none",
          resize: "vertical",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          boxSizing: "border-box",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = accentColor + "80";
          e.currentTarget.style.boxShadow = `0 0 0 3px ${accentColor}18`;
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
          e.currentTarget.style.boxShadow = "none";
        }}
      />
    </div>
  );
}

function FormSelect({
  label,
  options,
  required,
  accentColor,
}: {
  label: string;
  options: string[];
  required?: boolean;
  accentColor: string;
}) {
  return (
    <div>
      <label
        style={{
          display: "block",
          color: "#94A3B8",
          fontSize: "0.78rem",
          fontFamily: "'Space Grotesk', sans-serif",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginBottom: "0.4rem",
        }}
      >
        {label} {required && <span aria-hidden="true" style={{ color: accentColor }}>*</span>}
      </label>
      <select
        required={required}
        aria-required={required || undefined}
        style={{
          width: "100%",
          background: "rgba(20,26,46,0.95)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "0.6rem",
          padding: "0.7rem 0.9rem",
          color: "white",
          fontFamily: "'Inter', sans-serif",
          fontSize: "0.9rem",
          outline: "none",
          cursor: "pointer",
          transition: "border-color 0.2s ease",
          boxSizing: "border-box",
          appearance: "none",
          WebkitAppearance: "none",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = accentColor + "80";
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
        }}
      >
        <option value="" style={{ background: "#0D1220" }}>
          Select an option
        </option>
        {options.map((o) => (
          <option key={o} value={o} style={{ background: "#0D1220" }}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function CTADrawer({ open, onClose, variant }: CTADrawerProps) {
  const config = VARIANTS[variant];
  const [submitted, setSubmitted] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Reset submitted state when drawer closes
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => setSubmitted(false), 400);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    chiNotify.success("Your submission has been received!", {
      description: "A member of our team will be in touch shortly.",
    });
  };

  const renderFormFields = () => {
    switch (variant) {
      case "speaker":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.9rem" }}>
              <FormInput label="First Name" required accentColor={config.accentColor} />
              <FormInput label="Last Name" required accentColor={config.accentColor} />
            </div>
            <FormInput label="Email Address" type="email" required placeholder="Enter your email address" accentColor={config.accentColor} />
            <FormInput label="LinkedIn Profile" placeholder="Enter your LinkedIn profile URL" accentColor={config.accentColor} />
            <FormInput label="Company / Organization" accentColor={config.accentColor} />
            <FormInput label="Proposed Talk Title" required placeholder="Enter your proposed talk title" accentColor={config.accentColor} />
            <FormSelect
              label="Topic Area"
              required
              accentColor={config.accentColor}
              options={[
                "HR Leadership & Strategy",
                "Talent Acquisition & Retention",
                "DEI & Belonging",
                "People Analytics & AI",
                "Culture & Employee Experience",
                "Leadership Development",
                "Future of Work",
                "Other",
              ]}
            />
            <FormTextarea
              label="Talk Description"
              required
              rows={4}
              placeholder="Describe your proposed talk and what attendees will take away"
              accentColor={config.accentColor}
            />
          </div>
        );

      case "join-community":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.9rem" }}>
              <FormInput label="First Name" required accentColor={config.accentColor} />
              <FormInput label="Last Name" required accentColor={config.accentColor} />
            </div>
            <FormInput label="Email Address" type="email" required placeholder="Enter your email address" accentColor={config.accentColor} />
            <FormInput label="Job Title" placeholder="Enter your job title" accentColor={config.accentColor} />
            <FormInput label="Company / Organization" accentColor={config.accentColor} />
            <FormSelect
              label="Industry"
              accentColor={config.accentColor}
              options={[
                "Human Resources",
                "Talent & Recruiting",
                "L&D / Organizational Development",
                "Benefits & Wellness",
                "Operations & Business Leadership",
                "Entrepreneurship / Startup",
                "Consulting",
                "Education",
                "Other",
              ]}
            />
            <FormSelect
              label="How did you hear about us?"
              accentColor={config.accentColor}
              options={[
                "LinkedIn",
                "Referral from a member",
                "Social media",
                "Event or conference",
                "Web search",
                "Other",
              ]}
            />
            <FormTextarea
              label="Why do you want to join?"
              rows={3}
              placeholder="Describe what you are hoping to get from the community"
              accentColor={config.accentColor}
            />
          </div>
        );

      case "general-inquiry":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.9rem" }}>
              <FormInput label="First Name" required accentColor={config.accentColor} />
              <FormInput label="Last Name" required accentColor={config.accentColor} />
            </div>
            <FormInput label="Email Address" type="email" required placeholder="Enter your email address" accentColor={config.accentColor} />
            <FormInput label="Organization" accentColor={config.accentColor} />
            <FormSelect
              label="Inquiry Type"
              required
              accentColor={config.accentColor}
              options={[
                "General Question",
                "Event Information",
                "Membership Inquiry",
                "Partnership / Sponsorship",
                "Speaker Inquiry",
                "Media / Press",
                "Other",
              ]}
            />
            <FormTextarea
              label="Your Message"
              required
              rows={5}
              placeholder="Describe how we can help"
              accentColor={config.accentColor}
            />
          </div>
        );
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.65)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          zIndex: 9998,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.35s ease",
        }}
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(480px, 100vw)",
          background: "linear-gradient(180deg, #0D1220 0%, #080C18 100%)",
          borderLeft: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "-24px 0 80px rgba(0,0,0,0.6)",
          zIndex: 9999,
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.4s cubic-bezier(0.23,1,0.32,1)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Top accent bar */}
        <div
          style={{
            height: 3,
            background: `linear-gradient(90deg, ${config.gradientFrom}, ${config.gradientTo})`,
            flexShrink: 0,
          }}
        />

        {/* Header */}
        <div
          style={{
            padding: "1.5rem 1.75rem 1.25rem",
            borderBottom: "1px solid rgba(255,255,255,0.07)",
            flexShrink: 0,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "1rem",
          }}
        >
          <div>
            {/* Variant label pill */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem",
                background: `${config.accentColor}18`,
                border: `1px solid ${config.accentColor}35`,
                borderRadius: "2rem",
                padding: "0.25rem 0.75rem",
                marginBottom: "0.75rem",
                color: config.accentColor,
                fontSize: "0.72rem",
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {config.icon}
              {config.label}
            </div>
            <h2
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontWeight: 800,
                fontSize: "1.75rem",
                color: "white",
                lineHeight: 1.1,
                margin: 0,
              }}
            >
              {config.title}
            </h2>
            <p
              style={{
                color: "#64748B",
                fontSize: "var(--chi-body-compact)",
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.6,
                marginTop: "0.5rem",
                marginBottom: 0,
              }}
            >
              {config.subtitle}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#94A3B8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "background 0.15s ease, color 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.12)";
              e.currentTarget.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.06)";
              e.currentTarget.style.color = "#94A3B8";
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable form body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1.5rem 1.75rem" }}>
          {submitted ? (
            /* Success state */
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: "3rem 1rem",
                gap: "1.25rem",
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: `${config.accentColor}18`,
                  border: `2px solid ${config.accentColor}50`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: config.accentColor,
                }}
              >
                <CheckCircle size={32} />
              </div>
              <h3
                style={{
                  fontFamily: "'Syne', 'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: "1.3rem",
                  letterSpacing: "-0.04em",
                  color: "white",
                  margin: 0,
                }}
              >
                Submission Received!
              </h3>
              <p
                style={{
                  color: "#94A3B8",
                  fontSize: "var(--chi-body-compact)",
                  fontFamily: "'Inter', sans-serif",
                  lineHeight: 1.7,
                  maxWidth: "320px",
                  margin: 0,
                }}
              >
                Thank you for reaching out. A member of The CHI Alliance team will be in touch with you shortly.
              </p>
              <button
                onClick={onClose}
                className="chi-btn-outline"
                style={{ marginTop: "0.5rem", padding: "0.75rem 2rem" }}
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <p style={{ color: "#94A3B8", fontSize: "0.72rem", fontFamily: "'Inter', sans-serif", margin: "0 0 1rem" }}>
                <span aria-hidden="true" style={{ color: config.accentColor, fontWeight: 700 }}>*</span> Required field
              </p>
              {renderFormFields()}

              {/* POC disclaimer */}
              <p
                style={{
                  color: "#475569",
                  fontSize: "var(--chi-body-compact)",
                  fontFamily: "'Inter', sans-serif",
                  lineHeight: 1.6,
                  marginTop: "1.25rem",
                  padding: "0.75rem",
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <strong style={{ color: "#64748B" }}>POC Demo:</strong> This form is non-functional and for demonstration purposes only. No data will be submitted or stored.
              </p>

              {/* Submit button */}
              <button
                type="submit"
                className="chi-btn-primary"
                style={{
                  width: "100%",
                  marginTop: "1.5rem",
                  padding: "1rem",
                  fontSize: "0.9rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  background: `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`,
                }}
              >
                {config.submitLabel} <CtaIcon icon={variant === "speaker" ? Mic : variant === "join-community" ? Users : MessageSquare} />
              </button>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .cta-drawer-input:focus {
          border-color: rgba(233,30,140,0.5) !important;
        }
      `}</style>
    </>
  );
}
